import { Link } from 'react-router-dom';
import { useBlockNumber, useChainId } from 'wagmi';
import { Globe, IconButton, LiveDot, Logo, Telegram, XSocial } from '@/components/ui';
import { supportedChains } from '@/wagmi';

const PROTOCOL_LINKS = [
  { label: 'Explore', to: '/' },
  { label: 'Launch a token', to: '/launch' },
  { label: 'Portfolio', to: '/portfolio' },
  { label: 'Docs', href: '#' },
  { label: 'Audit', href: '#' },
] as const;

const RESOURCE_LINKS = [
  'How the floor works',
  'Anti-snipe',
  'Graduation',
  'Fees',
  'GitHub',
] as const;

const linkClass =
  'inline-block rounded text-sm text-muted transition-colors duration-200 ease-swift hover:text-fg';

export function Footer() {
  const chainId = useChainId();
  const { data: blockNumber } = useBlockNumber({ watch: true });
  const chain = supportedChains.find((c) => c.id === chainId);
  const chainName = chain?.name ?? 'Unknown network';

  return (
    <footer className="mt-14">
      <div className="shell">
        <div className="rule" />
      </div>

      <div className="shell grid gap-8 py-10 md:grid-cols-3 md:gap-6">
        {/* ── Brand ─────────────────────────────────────────────────────── */}
        <div className="max-w-sm">
          <div className="flex items-center gap-2">
            <Logo size={22} className="text-bull" />
            <span className="font-display text-sm font-extrabold leading-none tracking-tight">
              bull<span className="text-muted">.markets</span>
            </span>
          </div>
          <p className="mt-3 text-sm text-muted">
            Every launch, backed by a floor that only goes up.
          </p>
          <div className="mt-4 flex items-center gap-1">
            <IconButton label="bull.markets on X" size="sm" variant="ghost">
              <XSocial size={15} />
            </IconButton>
            <IconButton label="bull.markets on Telegram" size="sm" variant="ghost">
              <Telegram size={15} />
            </IconButton>
            <IconButton label="bull.markets website" size="sm" variant="ghost">
              <Globe size={15} />
            </IconButton>
          </div>
        </div>

        {/* ── Protocol ──────────────────────────────────────────────────── */}
        <nav aria-label="Protocol">
          <h2 className="text-3xs font-semibold uppercase tracking-[0.14em] text-subtle">
            Protocol
          </h2>
          <ul role="list" className="mt-3 space-y-2">
            {PROTOCOL_LINKS.map((item) => (
              <li key={item.label}>
                {'to' in item ? (
                  <Link to={item.to} className={linkClass}>
                    {item.label}
                  </Link>
                ) : (
                  <a href={item.href} className={linkClass}>
                    {item.label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* ── Resources ─────────────────────────────────────────────────── */}
        <nav aria-label="Resources">
          <h2 className="text-3xs font-semibold uppercase tracking-[0.14em] text-subtle">
            Resources
          </h2>
          <ul role="list" className="mt-3 space-y-2">
            {RESOURCE_LINKS.map((label) => (
              <li key={label}>
                <a href="#" className={linkClass}>
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* ── Bottom bar ──────────────────────────────────────────────────── */}
      <div className="border-t border-line">
        <div className="shell flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pb-24 pt-4 text-2xs text-subtle lg:pb-4">
          <p>© 2026 bull.markets</p>
          <p className="flex items-center gap-2">
            <LiveDot tone="up" />
            <span className="text-muted">{chainName}</span>
            <span aria-hidden="true">·</span>
            <span className="font-mono">
              block #{blockNumber !== undefined ? blockNumber.toString() : '—'}
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
